/**
 * CreateIdeaModal Component
 * 
 * Modal for creating a new idea with title, description, category, and priority.
 * Clean, focused UI for quick idea capture.
 */

import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  Modal,
  ScrollView,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, icon } from '../../../shared/tokens/mobile';
import { IdeaCategory, IdeaPriority, CreateIdeaRequest } from '../../../shared/types/idea';
import { CATEGORIES, PRIORITIES, CATEGORY_CONFIG, PRIORITY_CONFIG } from './ideaConfig';
import { styles } from './styles/createIdeaModalStyles';

// Default configs for safety
const DEFAULT_CATEGORY_CONFIG = { key: 'general' as IdeaCategory, label: 'General', icon: 'bulb-outline', color: '#6B6B6B' };
const DEFAULT_PRIORITY_CONFIG = { key: 'medium' as IdeaPriority, label: 'Medium', color: '#4A6B82' };

interface CreateIdeaModalProps {
  visible: boolean;
  onClose: () => void;
  onCreate: (data: CreateIdeaRequest) => Promise<boolean>;
  creating?: boolean;
  apiError?: string | null;
}

export function CreateIdeaModal({ visible, onClose, onCreate, creating, apiError }: CreateIdeaModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<IdeaCategory>('general');
  const [priority, setPriority] = useState<IdeaPriority>('medium');
  const [localError, setLocalError] = useState<string | null>(null);
  
  // Show API error or local validation error
  const error = apiError || localError;

  const resetForm = useCallback(() => {
    setTitle('');
    setDescription('');
    setCategory('general');
    setPriority('medium');
    setLocalError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleCreate = useCallback(async () => {
    if (!title.trim()) {
      setLocalError('Please enter a title');
      return;
    }

    setLocalError(null);
    await onCreate({
      title: title.trim(),
      description: description.trim() || undefined,
      category,
      priority,
    });
  }, [title, description, category, priority, onCreate]);

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={handleClose}
    >
      <KeyboardAvoidingView 
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={styles.container}
      >
        {/* Header */}
        <View style={styles.header}>
          <Pressable onPress={handleClose} style={styles.closeButton}>
            <Ionicons name="close" size={icon.lg} color={color.textPrimary} />
          </Pressable>
          <Text style={styles.headerTitle}>New Idea</Text>
          <Pressable 
            onPress={handleCreate} 
            disabled={creating || !title.trim()}
            style={[styles.createButton, (!title.trim() || creating) && styles.createButtonDisabled]}
          >
            {creating ? (
              <ActivityIndicator size="small" color={color.surface} />
            ) : (
              <Text style={styles.createButtonText}>Post</Text>
            )}
          </Pressable>
        </View>

        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          {/* Error */}
          {error && (
            <View style={styles.errorBanner}>
              <Ionicons name="alert-circle" size={icon.sm} color={color.error} />
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}

          {/* Title */}
          <View style={styles.field}>
            <Text style={styles.label}>Title *</Text>
            <TextInput
              style={styles.titleInput}
              placeholder="What's your idea?"
              placeholderTextColor={color.textMuted}
              value={title}
              onChangeText={setTitle}
              maxLength={255}
              autoFocus
            />
          </View>

          {/* Description */}
          <View style={styles.field}>
            <Text style={styles.label}>Description</Text>
            <TextInput
              style={styles.descriptionInput}
              placeholder="Add more details (optional)"
              placeholderTextColor={color.textMuted}
              value={description}
              onChangeText={setDescription}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          {/* Category */}
          <View style={styles.field}>
            <Text style={styles.label}>Category</Text>
            <View style={styles.optionsGrid}>
              {CATEGORIES.map(cat => {
                const isSelected = category === cat.key;
                const config = CATEGORY_CONFIG[cat.key] || DEFAULT_CATEGORY_CONFIG;
                return (
                  <Pressable
                    key={cat.key}
                    onPress={() => setCategory(cat.key)}
                    style={[
                      styles.optionButton,
                      isSelected && { backgroundColor: config.color + '15', borderColor: config.color },
                    ]}
                  >
                    <Ionicons 
                      name={config.icon as any} 
                      size={icon.md} 
                      color={isSelected ? config.color : color.textSecondary} 
                    />
                    <Text style={[styles.optionLabel, isSelected && { color: config.color }]}>
                      {config.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          {/* Priority */}
          <View style={styles.field}>
            <Text style={styles.label}>Priority</Text>
            <View style={styles.priorityRow}>
              {PRIORITIES.map(pri => {
                const isSelected = priority === pri.key;
                const config = PRIORITY_CONFIG[pri.key] || DEFAULT_PRIORITY_CONFIG;
                return (
                  <Pressable
                    key={pri.key}
                    onPress={() => setPriority(pri.key)}
                    style={[
                      styles.priorityButton,
                      isSelected && { backgroundColor: config.color + '15', borderColor: config.color },
                    ]}
                  >
                    <View style={[styles.priorityDot, { backgroundColor: config.color }]} />
                    <Text style={[styles.priorityLabel, isSelected && { color: config.color, fontWeight: '600' }]}>
                      {config.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </Modal>
  );
}
